import * as THREE from "three";
import { Environment, useVideoTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { Suspense, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { getLargestSupportedResolution } from "../Resolutions.jsx";
import Hls from "hls.js"; // Add this import at the top
import {DEFAULT_SCENE_MEDIA, DEFAULT_FADE_DURATION_MS, VIEW_MODE} from "./VideoConfig.jsx";


function FadeOverlay({ targetFade, onFadeOutComplete, fadeDurationMs }) {
	const materialRef = useRef();
	const meshRef = useRef();
	const camera = useThree((s) => s.camera);
	
    // 1. Change this from 0 to 1 so it starts fully black
	const fadeRef = useRef(1); 
	
	const onCompleteRef = useRef(onFadeOutComplete);
	useEffect(() => {
		onCompleteRef.current = onFadeOutComplete;
	}, [onFadeOutComplete]);

	useEffect(() => {
		if (targetFade === 1 && fadeRef.current === 1) {
			if (onCompleteRef.current) onCompleteRef.current();
		}
	}, [targetFade]);

	useFrame((_, delta) => {
		// ... (useFrame logic stays exactly the same)
		if (meshRef.current) {
			meshRef.current.position.copy(camera.position);
		}
		if (materialRef.current) {
			const durationSec = fadeDurationMs / 1000;
			const step = durationSec > 0 ? (delta / durationSec) : 1; 
			
			let current = fadeRef.current;

			if (current < targetFade) {
				current = Math.min(current + step, targetFade);
				if (current === targetFade && onCompleteRef.current) {
					onCompleteRef.current();
				}
			} else if (current > targetFade) {
				current = Math.max(current - step, targetFade);
			}

			fadeRef.current = current;
			materialRef.current.opacity = current;
			materialRef.current.visible = current > 0;
		}
	});

	return (
		<mesh ref={meshRef} renderOrder={9999}>
			<sphereGeometry args={[2, 16, 16]} />
			<meshBasicMaterial
				ref={materialRef}
				color="white"
				transparent
                // 2. Change opacity from 0 to 1 so the material loads in black
				opacity={1} 
				side={THREE.BackSide}
				depthTest={false}
				depthWrite={false}
				toneMapped={false}
			/>
		</mesh>
	);
}
function FlatVideoPlane({ src, onVideoReady }) {
	const planeRef = useRef(null);
	const camera = useThree((s) => s.camera);
	const [texture, setTexture] = useState(null);

	const onVideoReadyRef = useRef(onVideoReady);
	useEffect(() => {
		onVideoReadyRef.current = onVideoReady;
	}, [onVideoReady]);

	useEffect(() => {
        // 1. EXACT SAME VIDEO LOGIC as SkyboxVideoSphere
		const video = document.createElement("video");
		video.crossOrigin = "anonymous";
		video.playsInline = true;
		video.muted = true;
		video.loop = true;
		video.style.cssText = "position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1;";
		document.body.appendChild(video);

		const newTexture = new THREE.VideoTexture(video);
		newTexture.colorSpace = THREE.SRGBColorSpace;
		newTexture.minFilter = THREE.LinearFilter;
		newTexture.magFilter = THREE.LinearFilter;
		newTexture.generateMipmaps = false; 
		
		setTexture(newTexture);

		let hls;
		let isReady = false;
		let failsafeTimeout;

		const notifyReady = () => {
			if (isReady) return;
			isReady = true;
			clearTimeout(failsafeTimeout);
			if (onVideoReadyRef.current) onVideoReadyRef.current();
		};

		failsafeTimeout = setTimeout(() => {
			console.warn("Video buffering timeout - forcing fade in to prevent black screen.");
			notifyReady();
		}, 4000);

		const attemptPlay = () => {
			const playPromise = video.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					console.log("Play failed or waiting for user interaction.");
					notifyReady(); 
				});
			}
		};

		const checkTime = () => {
			if (video.currentTime > 0) {
				notifyReady();
				video.removeEventListener("timeupdate", checkTime);
			}
		};
		video.addEventListener("timeupdate", checkTime);
		video.addEventListener("playing", notifyReady);

		if (Hls.isSupported()) {
			hls = new Hls({ 
				startLevel: -1, 
				capLevelToPlayerSize: false, 
				maxBufferLength: 30, 
				maxMaxBufferLength: 60, 
				maxBufferSize: 60 * 1000 * 1000, 
				lowLatencyMode: false, 
				smoothQualityChange: true 
			});
			hls.loadSource(src);
			hls.attachMedia(video);
			hls.on(Hls.Events.MANIFEST_PARSED, attemptPlay);
		} 
		else if (video.canPlayType("application/vnd.apple.mpegurl")) {
			video.src = src;
			video.addEventListener("loadedmetadata", attemptPlay);
		}

		const resume = () => attemptPlay();
		window.addEventListener("pointerdown", resume, { once: true });
		window.addEventListener("touchstart", resume, { once: true });

		return () => {
			clearTimeout(failsafeTimeout);
			window.removeEventListener("pointerdown", resume);
			window.removeEventListener("touchstart", resume);
			video.removeEventListener("timeupdate", checkTime);
			video.removeEventListener("playing", notifyReady);
			
			if (hls) hls.destroy(); 
			
			video.pause();
			video.removeAttribute("src");
			video.load();
			video.remove();
			
			newTexture.dispose();
		};
	}, [src]);

	useFrame(() => {
		if (planeRef.current && texture) {
            // 2. Lock plane to camera position and rotation
			planeRef.current.position.copy(camera.position);
			planeRef.current.quaternion.copy(camera.quaternion);
            
            // Push it 10 units in front of the camera
			planeRef.current.translateZ(-10);

            // 3. Calculate exact width and height to fill the frustum at z = -10
			const fovInRadians = camera.fov * THREE.MathUtils.DEG2RAD;
			const frustumHeight = 2 * Math.tan(fovInRadians / 2) * 10;
			const frustumWidth = frustumHeight * camera.aspect;

            // 4. Mimic CSS "Object-Fit: Cover" to prevent black bars if video aspect != screen aspect
            let videoAspect = 16 / 9; // Fallback
            if (texture.image && texture.image.videoWidth) {
                videoAspect = texture.image.videoWidth / texture.image.videoHeight;
            }

            let finalWidth = frustumWidth;
            let finalHeight = frustumHeight;

            if (camera.aspect > videoAspect) {
                // Screen is wider than video -> scale based on width to cover height
                finalHeight = frustumWidth / videoAspect;
            } else {
                // Screen is taller than video -> scale based on height to cover width
                finalWidth = frustumHeight * videoAspect;
            }

            planeRef.current.scale.set(finalWidth, finalHeight, 1);
		}
	});

	return (
		<mesh ref={planeRef} frustumCulled={false} renderOrder={-1000}>
			<planeGeometry args={[1, 1]} />
			{texture && (
				<meshBasicMaterial
					map={texture}
					side={THREE.FrontSide}
					toneMapped={false}
					depthWrite={false}
				/>
			)}
		</mesh>
	);
}
export function LocationSkyboxVideo({ 
    src = DEFAULT_SCENE_MEDIA.videoSrc,
    viewMode = VIEW_MODE.PEDESTRIAN, // <-- Add this prop
    fadeDurationMs = DEFAULT_FADE_DURATION_MS 
}) {
	console.debug("LocationSkyboxVideo: Rendering with src:", src, "mode:", viewMode);
	const [activeSrc, setActiveSrc] = useState(src);
    const [activeMode, setActiveMode] = useState(viewMode); // Track mode for smooth fade
    
	const [targetFade, setTargetFade] = useState(1);

	useEffect(() => {
        // Trigger fade if either the source or the view mode changes
		if (src !== activeSrc || viewMode !== activeMode) {
			setTargetFade(1); 
		}
	}, [src, activeSrc, viewMode, activeMode]);

	const handleFadeOutComplete = useCallback(() => {
		setActiveSrc(src); 
        setActiveMode(viewMode); // Apply the new view mode while screen is black
	}, [src, viewMode]);

	const handleVideoReady = useCallback(() => {
		setTargetFade(0); 
	}, []);

	return (
		<Suspense fallback={null}>
			<SkyboxVideoSphere src={activeSrc} onVideoReady={handleVideoReady} />
            {/* {activeMode === VIEW_MODE.PEDESTRIAN ? (
                <SkyboxVideoSphere src={activeSrc} onVideoReady={handleVideoReady} />
            ) : (
                <FlatVideoPlane src={activeSrc} onVideoReady={handleVideoReady} />
            )} */}
			
			<FadeOverlay 
                targetFade={targetFade} 
                onFadeOutComplete={handleFadeOutComplete} 
                fadeDurationMs={fadeDurationMs} 
            />
		</Suspense>
	);
}
export function LocationSkybox({ location, inOverview, grayscale = true }) {
	const gl = useThree(state => state.gl);
	const ctx = gl.getContext();
	const resolution = getLargestSupportedResolution(ctx, [512, 1024]);

	const style = grayscale ? 'grayscale' : 'color';

	return <Environment
		background={!inOverview}
		files={[
			`./images/locations/${resolution}/${style}/${location}/px.jpg`,
			`./images/locations/${resolution}/${style}/${location}/nx.jpg`,
			`./images/locations/${resolution}/${style}/${location}/py.jpg`,
			`./images/locations/${resolution}/${style}/${location}/ny.jpg`,
			`./images/locations/${resolution}/${style}/${location}/pz.jpg`,
			`./images/locations/${resolution}/${style}/${location}/nz.jpg`,
		]}
	/>
};

function SkyboxVideoSphereMP4({ src }) {
	const sphereRef = useRef(null);
	const camera = useThree((s) => s.camera);

	const texture = useVideoTexture(src, {
		muted: true,
		loop: true,
		start: true,
		playsInline: true,
		crossOrigin: "anonymous",
		unsuspend: "canplay",
	});

	useEffect(() => {
		const video = texture.image;
		if (!(video instanceof HTMLVideoElement)) return;

		video.id = "skybox-360-video";
		video.setAttribute("data-r3f-skybox", "true");
		video.style.cssText =
			"position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1;";
		document.body.appendChild(video);

		const resume = () => void video.play().catch(() => {});
		window.addEventListener("pointerdown", resume, { once: true });
		window.addEventListener("touchstart", resume, { once: true });
		window.addEventListener("keydown", resume, { once: true });

		return () => {
			window.removeEventListener("pointerdown", resume);
			window.removeEventListener("touchstart", resume);
			window.removeEventListener("keydown", resume);
			// CLEAR MEMORY BUFFER: Remove source and force reload to clear memory
			video.removeAttribute("src");
			video.load();
			video.pause();
			video.remove();
			// 2. Clear GPU VRAM (CRITICAL FOR MOBILE)
			texture.dispose();
		};
	}, [texture]);

	useFrame(() => {
		if (!sphereRef.current) return;
		sphereRef.current.position.copy(camera.position);
		const v = texture.image;
		if (v instanceof HTMLVideoElement && v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
			texture.needsUpdate = true;
		}
	});

	return (
		<mesh ref={sphereRef} frustumCulled={false} renderOrder={-1000}>
			<sphereGeometry args={[600, 64, 64]} />
			<meshBasicMaterial
				map={texture}
				side={THREE.BackSide}
				toneMapped={false}
				depthWrite={false}
			/>
		</mesh>
	);
}

function SkyboxVideoSphereUnoptimized({ src }) {
	const sphereRef = useRef(null);
	const camera = useThree((s) => s.camera);
	
	// Store the finished texture in state, not the video element
	const [texture, setTexture] = useState(null);

	useEffect(() => {
		// 1. Create the video element
		const video = document.createElement("video");
		video.crossOrigin = "anonymous";
		video.playsInline = true;
		video.muted = true;
		video.loop = true;
		video.id = "skybox-hls-video";
		video.style.cssText =
			"position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1;";
		document.body.appendChild(video);

		// 2. Create the Three.js texture immediately and save it
		const newTexture = new THREE.VideoTexture(video);
		newTexture.colorSpace = THREE.SRGBColorSpace;
		setTexture(newTexture);

		let hls;

		// Helper to safely play
		const attemptPlay = () => {
			video.play().catch(() => console.log("Waiting for user interaction to play."));
		};

		// 3. Setup HLS
		if (Hls.isSupported()) {
			hls = new Hls({ startLevel: -1 });
			hls.loadSource(src);
			hls.attachMedia(video);
			hls.on(Hls.Events.MANIFEST_PARSED, attemptPlay);
		} 
		else if (video.canPlayType("application/vnd.apple.mpegurl")) {
			video.src = src;
			video.addEventListener("loadedmetadata", attemptPlay);
		}

		// Mobile autoplay interactions
		const resume = () => attemptPlay();
		window.addEventListener("pointerdown", resume, { once: true });
		window.addEventListener("touchstart", resume, { once: true });

		// 4. CLEANUP (Only runs when 'src' changes or component unmounts)
		return () => {
			window.removeEventListener("pointerdown", resume);
			window.removeEventListener("touchstart", resume);
			
			if (hls) hls.destroy(); 
			
			video.pause();
			video.removeAttribute("src");
			video.load();
			video.remove();
			
			newTexture.dispose();
		};
	}, [src]); // CRITICAL FIX: Only run this effect when 'src' changes!

	// Stick to the camera
	useFrame(() => {
		if (sphereRef.current) sphereRef.current.position.copy(camera.position);
	});

	return (
		<mesh ref={sphereRef} frustumCulled={false} renderOrder={-1000} scale={[-1,1,1]}>
			<sphereGeometry args={[600, 64, 64]} />
			{texture && (
				<meshBasicMaterial
					map={texture}
					side={THREE.BackSide}
					toneMapped={false}
					depthWrite={false}
				/>
			)}
		</mesh>
	);
}
function SkyboxVideoSphere({ src , onVideoReady}) {
	const sphereRef = useRef(null);
	const camera = useThree((s) => s.camera);
	const [texture, setTexture] = useState(null);

	const onVideoReadyRef = useRef(onVideoReady);
	useEffect(() => {
		onVideoReadyRef.current = onVideoReady;
	}, [onVideoReady]);

	useEffect(() => {
		const video = document.createElement("video");
		video.crossOrigin = "anonymous";
		video.playsInline = true;
		video.muted = true;
		video.loop = true;
		video.style.cssText = "position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1;";
		document.body.appendChild(video);

		const newTexture = new THREE.VideoTexture(video);
		newTexture.colorSpace = THREE.SRGBColorSpace;
		newTexture.minFilter = THREE.LinearFilter;
		newTexture.magFilter = THREE.LinearFilter;
		newTexture.generateMipmaps = false; 
		
		setTexture(newTexture);

		let hls;
		let isReady = false;
		let failsafeTimeout;

		const notifyReady = () => {
			if (isReady) return; // Prevent double-firing
			isReady = true;
			clearTimeout(failsafeTimeout);
			if (onVideoReadyRef.current) onVideoReadyRef.current();
		};

		// 1. FAILSAFE: If video hangs or network is slow, force fade-in after 4 seconds
		failsafeTimeout = setTimeout(() => {
			console.warn("Video buffering timeout - forcing fade in to prevent black screen.");
			notifyReady();
		}, 4000);

		const attemptPlay = () => {
			const playPromise = video.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					console.log("Play failed or waiting for user interaction.");
					notifyReady(); // Trigger fade-in anyway
				});
			}
		};

		// 2. THE MOST RELIABLE CHECK: Wait until the video clock actually moves past 0
		const checkTime = () => {
			if (video.currentTime > 0) {
				notifyReady();
				video.removeEventListener("timeupdate", checkTime);
			}
		};
		video.addEventListener("timeupdate", checkTime);
		
		// Fallback check
		video.addEventListener("playing", notifyReady);

		if (Hls.isSupported()) {
			hls = new Hls({ 
				startLevel: -1, 
				capLevelToPlayerSize: false, 
				maxBufferLength: 30, 
				maxMaxBufferLength: 60, 
				maxBufferSize: 60 * 1000 * 1000, 
				lowLatencyMode: false, 
				smoothQualityChange: true 
			});
			hls.loadSource(src);
			hls.attachMedia(video);
			hls.on(Hls.Events.MANIFEST_PARSED, attemptPlay);
		} 
		else if (video.canPlayType("application/vnd.apple.mpegurl")) {
			video.src = src;
			video.addEventListener("loadedmetadata", attemptPlay);
		}

		const resume = () => attemptPlay();
		window.addEventListener("pointerdown", resume, { once: true });
		window.addEventListener("touchstart", resume, { once: true });

		return () => {
			clearTimeout(failsafeTimeout);
			window.removeEventListener("pointerdown", resume);
			window.removeEventListener("touchstart", resume);
			video.removeEventListener("timeupdate", checkTime);
			video.removeEventListener("playing", notifyReady);
			
			if (hls) hls.destroy(); 
			
			video.pause();
			video.removeAttribute("src");
			video.load();
			video.remove();
			
			newTexture.dispose();
		};
	}, [src]);

	useFrame(() => {
		if (sphereRef.current) sphereRef.current.position.copy(camera.position);
	});

	return (
		<mesh ref={sphereRef} frustumCulled={false} renderOrder={-1000} scale={[-1,1,1]}>
			<sphereGeometry args={[600, 64, 64]} />
			{texture && (
				<meshBasicMaterial
					map={texture}
					side={THREE.BackSide}
					toneMapped={false}
					depthWrite={false}
				/>
			)}
		</mesh>
	);
}